/**
 * AiWizardStatsBlock
 * ==================
 *
 * Simple V1 stats block displayed on the Forms page (/forms) when the
 * workspace owns at least one AI Estimation form. Read-only, no charts.
 *
 * Metrics: started, completed, completion rate, abandons,
 * abandons by step, AI leads generated.
 */
import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { wizardStatsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sparkles, Play, CheckCircle, Percent, LogOut, Users } from 'lucide-react';

export function AiWizardStatsBlock() {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    wizardStatsAPI
      .get()
      .then((res) => { if (!cancelled) setStats(res.data); })
      .catch(() => { /* silent — block simply won't render data */ })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (isLoading || !stats) return null;

  const tiles = [
    {
      key: 'started',
      icon: Play,
      label: fr ? 'Wizards démarrés' : 'Wizards started',
      value: stats.started,
    },
    {
      key: 'completed',
      icon: CheckCircle,
      label: fr ? 'Wizards complétés' : 'Wizards completed',
      value: stats.completed,
    },
    {
      key: 'completion-rate',
      icon: Percent,
      label: fr ? 'Taux de complétion' : 'Completion rate',
      value: `${stats.completion_rate}%`,
    },
    {
      key: 'abandons',
      icon: LogOut,
      label: fr ? 'Abandons' : 'Abandons',
      value: stats.abandons,
    },
    {
      key: 'ai-leads',
      icon: Users,
      label: fr ? 'Leads IA générés' : 'AI leads generated',
      value: stats.ai_leads,
    },
  ];

  return (
    <Card className="border-amber-500/30 bg-amber-500/5" data-testid="ai-wizard-stats-block">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-bold flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-500" />
          {fr ? 'Statistiques — Formulaires IA' : 'Stats — AI Forms'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Metric tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {tiles.map(({ key, icon: Icon, label, value }) => (
            <div
              key={key}
              className="p-3 rounded-lg border bg-background"
              data-testid={`wizard-stat-${key}`}
            >
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                <Icon className="h-3.5 w-3.5" />
                {label}
              </div>
              <div className="text-2xl font-black tabular-nums">{value}</div>
            </div>
          ))}
        </div>

        {/* Per-form breakdown — Pavé Uni and Fissures/Drains never mixed */}
        {(stats.forms || []).length > 0 && (
          <div className="space-y-3" data-testid="wizard-stats-per-form">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              {fr ? 'Détail par formulaire' : 'Per-form breakdown'}
            </p>
            {stats.forms.map((f) => (
              <div
                key={f.form_id}
                className="rounded-lg border bg-background p-3 space-y-2"
                data-testid={`wizard-stats-form-${f.form_id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-semibold text-sm">{f.form_name}</span>
                  <span className="text-[10px] font-mono text-muted-foreground">
                    {f.preset_id} · /{f.form_slug}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
                  {[
                    [fr ? 'Démarrés' : 'Started', f.started],
                    [fr ? 'Complétés' : 'Completed', f.completed],
                    [fr ? 'Taux' : 'Rate', `${f.completion_rate}%`],
                    [fr ? 'Abandons' : 'Abandons', f.abandons],
                    [fr ? 'Leads' : 'Leads', f.ai_leads],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded border px-2 py-1.5">
                      <p className="text-[10px] text-muted-foreground">{label}</p>
                      <p className="font-bold tabular-nums text-sm">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Abandons by step */}
        <div data-testid="wizard-stat-abandons-by-step">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            {fr ? 'Abandons par étape' : 'Abandons by step'}
          </p>
          {(stats.abandons_by_step || []).length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              {fr ? 'Aucun abandon enregistré.' : 'No abandons recorded.'}
            </p>
          ) : (
            <div className="space-y-1.5">
              {stats.abandons_by_step.map((row) => (
                <div
                  key={row.step_index}
                  className="flex items-center justify-between text-sm px-3 py-1.5 rounded border bg-background"
                  data-testid={`wizard-abandon-step-${row.step_number}`}
                >
                  <span>
                    {fr ? 'Étape' : 'Step'} {row.step_number}
                    {row.step_label ? ` — ${row.step_label}` : ''}
                  </span>
                  <span className="font-bold tabular-nums">{row.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
